import { Images, Search, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import { MasteryPill } from "../components/MasteryPill";
import {
  birdLabel,
  birds,
  groupMeta,
  type Bird,
  type BirdGroup
} from "../data/birds";
import type { ProgressState } from "../hooks/useProgress";

type LibraryViewProps = {
  progress: ProgressState;
  initialGroup?: BirdGroup;
  onOpenBird: (bird: Bird) => void;
};

type StatusFilter = "all" | "unseen" | "known" | "fuzzy" | "new";

export const LibraryView = ({
  progress,
  initialGroup,
  onOpenBird
}: LibraryViewProps) => {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<BirdGroup | "all">(initialGroup ?? "all");
  const [status, setStatus] = useState<StatusFilter>("all");

  const filteredBirds = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return birds.filter((bird) => {
      const matchesQuery =
        !normalizedQuery ||
        birdLabel(bird).toLowerCase().includes(normalizedQuery) ||
        bird.similar.name.toLowerCase().includes(normalizedQuery) ||
        bird.habitat.toLowerCase().includes(normalizedQuery);
      const matchesGroup = group === "all" || bird.group === group;
      const mastery = progress.mastery[bird.id];
      const matchesStatus =
        status === "all" ||
        (status === "unseen" ? !mastery : mastery === status);
      return matchesQuery && matchesGroup && matchesStatus;
    });
  }, [group, progress.mastery, query, status]);

  const clearFilters = () => {
    setQuery("");
    setGroup("all");
    setStatus("all");
  };

  const hasFilters = query || group !== "all" || status !== "all";

  return (
    <main className="page library-page">
      <header className="page-heading library-heading">
        <div>
          <span className="section-kicker">鸟类图鉴</span>
          <h1>61 张图卡，随时翻查</h1>
          <p>搜索名称，也可以从生活环境和学习状态缩小范围。</p>
        </div>
        <div className="library-heading__count">
          <strong>{filteredBirds.length}</strong>
          <span>当前结果</span>
        </div>
      </header>

      <section className="library-tools">
        <label className="search-box">
          <Search size={20} />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索鸟名、相似鸟或栖息环境"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="清除搜索">
              <X size={17} />
            </button>
          )}
        </label>
        <div className="filter-row">
          <span>
            <SlidersHorizontal size={17} />
            学习状态
          </span>
          {(
            [
              ["all", "全部"],
              ["unseen", "未学习"],
              ["new", "学习中"],
              ["fuzzy", "待巩固"],
              ["known", "已认识"]
            ] as [StatusFilter, string][]
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={status === value ? "is-active" : ""}
              onClick={() => setStatus(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="filter-row filter-row--groups">
          <span>观察场景</span>
          <button
            type="button"
            className={group === "all" ? "is-active" : ""}
            onClick={() => setGroup("all")}
          >
            全部主题
          </button>
          {(Object.keys(groupMeta) as BirdGroup[]).map((groupName) => (
            <button
              type="button"
              key={groupName}
              className={group === groupName ? "is-active" : ""}
              onClick={() => setGroup(groupName)}
            >
              {groupMeta[groupName].short}
            </button>
          ))}
        </div>
      </section>

      {filteredBirds.length ? (
        <section className="bird-grid">
          {filteredBirds.map((bird) => (
            <button
              className="bird-tile"
              key={bird.id}
              onClick={() => onOpenBird(bird)}
            >
              <span className="bird-tile__image">
                <img
                  src={bird.images[0]}
                  alt={birdLabel(bird)}
                  loading="lazy"
                />
                <MasteryPill mastery={progress.mastery[bird.id]} />
                {bird.images.length > 1 && (
                  <span className="bird-tile__multi">
                    <Images size={14} />
                    {bird.images.length}
                  </span>
                )}
              </span>
              <span className="bird-tile__content">
                <small>
                  {bird.id} · {bird.habitat}
                </small>
                <strong>{bird.name}</strong>
                {bird.variant && <em>{bird.variant}</em>}
                <span>{bird.memory}</span>
              </span>
            </button>
          ))}
        </section>
      ) : (
        <section className="empty-state">
          <Search size={28} />
          <h2>没有符合条件的鸟</h2>
          <p>换一个关键词，或清除当前筛选条件。</p>
          {hasFilters && (
            <button className="button button--ghost" onClick={clearFilters}>
              清除筛选
            </button>
          )}
        </section>
      )}
    </main>
  );
};
